import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { BuildingEfficiencyWizardService } from '../building-efficiency-wizard.component.service';
import { BuildingEfficiencyWizardStore } from '../building-efficiency-wizard.component.store';
import { ElectricityMeterSelectionService } from './electricity-meter-selection.service';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { MeterView, MeterViewResponse } from '@app/shared/models/meter-data/meter-view';
import { EfficiencyAnalysisMeters } from '@app/shared/models/efficiency-analysis/efficiency-analysis-meters';
import { DateUtilities } from '@app/shared/date-utility';
import { DotDayMonthYearFormat } from '@app/shared/constants';
import { EfficiencyAnalysisElectricityMeterConfig } from '@app/shared/enums';

@Component({
  selector: 'app-electricity-meter-selection',
  templateUrl: './electricity-meter-selection.component.html',
  styleUrls: ['./electricity-meter-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ElectricityMeterSelectionComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() savedEAReferenceData: any;
  @Output() nextButtonStatus = new EventEmitter<boolean>();
  public electricityMeterConfig = EfficiencyAnalysisElectricityMeterConfig;
  data: IRawWizardData | null = null;
  startDate?: string;
  endDate?: string;
  color = 'primary';
  sum = 0;
  meterTemplateConfig: string = '';
  electricityForm: FormGroup = new FormGroup({});
  note = this.translate.instant('module.shared.buildingEfficiencyWizard.step2.noteText');
  meters: EfficiencyAnalysisMeters[] = [];
  isConsumptionAutoSuggested = false;
  hasSuggestedConsumptions = false;
  initialTotalConsumption = 0;
  isInitialLoad = true;

  constructor(
    public electricityMeterSelection: ElectricityMeterSelectionService,
    public localNumberPipe: LocalNumberPipe,
    public translate: TranslateService,
    private _buildingEfficiencyWizardService: BuildingEfficiencyWizardService,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore,
    private _overlayLoader: OverlayLoaderService,
    private _userMunicipality: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    combineLatest([
      this.electricityMeterSelection.meters,
      this.electricityMeterSelection.isConsumptionAutoSuggested,
      this.electricityMeterSelection.hasSuggestedConsumptions,
    ])
      .pipe(
        finalize(() => {
          this._overlayLoader.ignoreChanges = false;
          this._overlayLoader.isLoading = false;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([meters, isConsumptionAutoSuggested, hasSuggestedConsumptions]: [EfficiencyAnalysisMeters[], boolean, boolean]) => {
        this.meters = meters;
        this.sum = this.calculateTotalConsumption(this.meters);
        this.isConsumptionAutoSuggested = isConsumptionAutoSuggested;
        this.hasSuggestedConsumptions = hasSuggestedConsumptions;
        this.loadMeterTemplateConfig(this.meters);
        this.validate(this.sum, meters);
      });

    this.startDate = this.getDisplayDate(this.electricityMeterSelection.startDate);
    this.endDate = this.getDisplayDate(this.electricityMeterSelection.endDate);

    this.onToggleChange(this.isConsumptionAutoSuggested);

    this.electricityMeterSelection.meters.pipe(takeUntil(this.unsubscribe$)).subscribe((eaMeters: EfficiencyAnalysisMeters[]) => {
      eaMeters.forEach((meter: EfficiencyAnalysisMeters) => {
        if (
          this.isInitialLoad &&
          !this.electricityMeterSelection.keyPressed &&
          (!isNil(meter.value) || (isNil(meter.isSelected) && this.isPropNotValid(meter.meterNumber) && !isNil(meter.value) && meter.value !== ''))
        ) {
          meter.value = this.unlocalize(meter.value);
          meter.value = this.localize(meter.value);
        }
      });
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public update(event: string | null = null, meter: EfficiencyAnalysisMeters | null = null): void {
    this.electricityMeterSelection.setMeters(this.meters);
    this.validate(this.sum, this.meters);
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(true);
  }

  public updateCheckbox(): void {
    this.update();
    this.electricityMeterSelection.setIsConsumptionAutoSuggested(false);
  }

  public change(): void {
    this.electricityMeterSelection.setIsConsumptionAutoSuggested(false);
    this.update();
  }

  public focus(input: any): void {
    this.isInitialLoad = false;
    const value = input.target.value;
    if (!isNil(value) && value !== '') {
      input.target.value = parseInt(input.target.value.toString().replace(',0', '').split(',').join('').split('.').join(''), 10);
    }
  }

  public blur(input: any): void {
    const value = input.target.value;
    if (!isNil(value) && value !== '') {
      input.target.value = this.localNumberPipe.transform(parseInt(input.target.value, 10));
    }
  }

  public onKeyPress(event: KeyboardEvent, meter: EfficiencyAnalysisMeters): boolean {
    if (this.numberOnly(event)) {
      this.electricityMeterSelection.keyPressed = true;
      this.setInfoMessage(meter.meter);
      meter.isSelected = !isNil(meter.value);
      this.electricityMeterSelection.setIsConsumptionAutoSuggested(false);
      return true;
    }

    return false;
  }

  public onToggleChange(toggled: boolean): void {
    this.electricityMeterSelection.keyPressed = false;
    if (toggled) {
      this._overlayLoader.isLoading = true;
      this.isInitialLoad = true;
      this.electricityMeterSelection.setIsConsumptionAutoSuggested(true);
      of(this.meters)
        .pipe(
          mergeMap((meters: EfficiencyAnalysisMeters[]) =>
            combineLatest(
              meters.map((meter: EfficiencyAnalysisMeters) =>
                this.getElectricityConsumptionForEA(
                  meter,
                  this.getMeterDate(this._buildingEfficiencyWizardStore.electirictyWizardData as IRawWizardData, true),
                  this.getMeterDate(this._buildingEfficiencyWizardStore.electirictyWizardData as IRawWizardData, false)
                )
              )
            )
          ),
          finalize(() => (this._overlayLoader.isLoading = false)),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    } else {
      this.isInitialLoad = false;
      this.meters.forEach((eam: EfficiencyAnalysisMeters) => {
        if (eam.meter) {
          eam.meter.message = undefined;
        }
        const hasValidValue = !isNil(eam.value) && eam.value !== '';
        if (hasValidValue || (isNil(eam.isSelected) && this.isPropNotValid(eam.meterNumber) && hasValidValue)) {
          eam.value = this.unlocalize(eam.value);
          eam.value = this.localize(eam.value);
        }
      });
    }
  }

  public getToggleVisibility(): boolean {
    if (this.meterTemplateConfig !== EfficiencyAnalysisElectricityMeterConfig.none && this.hasSuggestedConsumptions) {
      if (!isNil(this.savedEAReferenceData.efficiencyAnalysisDate) && isNil(this.savedEAReferenceData.isElectricityValueSuggested)) {
        if (!this.areDatesSimilar(this.electricityMeterSelection.startDate, this.savedEAReferenceData.efficiencyAnalysisStartDate)) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  private getElectricityConsumptionForEA(meter: EfficiencyAnalysisMeters, startDate: Date, endDate: Date): Observable<number> {
    return this._buildingEfficiencyWizardService.getConsumption(meter.meter, startDate, endDate).pipe(
      map((consumptionValue: number) => {
        if (consumptionValue === 0) {
          throw Error('No value');
        } else {
          return consumptionValue;
        }
      }),
      catchError(() => this.findHistoricalMetersForEA(meter, endDate)),
      tap((consumption: number) => {
        meter.value = consumption;
        meter.isSelected = !isNil(consumption);
        this.update();
      })
    );
  }

  private findHistoricalMetersForEA(
    meter: EfficiencyAnalysisMeters,
    endDate: Date,
    municipalityKey: string = this._userMunicipality.currentMunicipality.key
  ): Observable<number> {
    this.setInfoMessage(meter.meter);
    return this._buildingEfficiencyWizardService.getMetersByMelo(meter.meter.measurementLocationKey, municipalityKey).pipe(
      map((resp: MeterViewResponse) => this.findLatestHistoricalMeterForEA(resp.data, endDate)),
      mergeMap((meterView: MeterView) =>
        this._buildingEfficiencyWizardService.getConsumption(
          meterView,
          this.getMeterDate(this._buildingEfficiencyWizardStore.electirictyWizardData as IRawWizardData, true),
          this.getMeterDate(this._buildingEfficiencyWizardStore.electirictyWizardData as IRawWizardData, false)
        )
      ),
      tap(() => this.setHistoricDataInfoMessage(meter.meter)),
      catchError(() => {
        this.setNoHistoricDataInfoMessage(meter.meter);
        meter.value = null;
        return of(0);
      })
    );
  }

  private findLatestHistoricalMeterForEA(meters: MeterView[], endDate: Date): MeterView {
    return meters.find(
      (meter: MeterView) =>
        moment(meter.usageStartDate).toDate().getFullYear() <= moment(endDate).toDate().getFullYear() &&
        moment(meter.usageEndDate).toDate().getFullYear() >= moment(endDate).toDate().getFullYear()
    ) as MeterView;
  }

  private setNoHistoricDataInfoMessage(meter: MeterView): void {
    this.setInfoMessage(meter, this.translate.instant('module.shared.buildingEfficiencyWizard.step3.hasNoConsumptionAndHistoricalTooltipText'));
  }

  private setHistoricDataInfoMessage(meter: MeterView): void {
    this.setInfoMessage(meter, this.translate.instant('module.shared.buildingEfficiencyWizard.step3.hasConsumptionTooltipText'));
  }

  private setInfoMessage(meter: MeterView, message?: string): void {
    meter.message = message;
  }

  private getMeterDate(wizardData: IRawWizardData, isStartDate: boolean): Date {
    let dateToUse: moment.Moment;

    if (wizardData.hasTimePeriod) {
      dateToUse = isStartDate ? moment(wizardData.userDefinedStartDate) : moment(wizardData.userDefinedEndDate);
    } else {
      dateToUse = isStartDate ? moment(wizardData.defaultStartDate) : moment(wizardData.defaultEndDate);
    }

    return moment(dateToUse).toDate();
  }

  private calculateTotalConsumption(meters: EfficiencyAnalysisMeters[]): number {
    let sum = 0;
    meters.forEach((eaMeters: EfficiencyAnalysisMeters) => {
      const hasValidValue = !isNil(eaMeters.value) && eaMeters.value !== '';
      if ((hasValidValue && eaMeters.isSelected) || (isNil(eaMeters.isSelected) && this.isPropNotValid(eaMeters.meterNumber) && hasValidValue)) {
        if (typeof eaMeters.value === 'number') {
          eaMeters.value = eaMeters.value.toFixed(0);
        }
        sum += this.unlocalize(eaMeters.value);
      }
    });
    this.electricityMeterSelection.totalConsumptionStore.next(sum);
    return sum;
  }

  private localize(value: any): any {
    return this.localNumberPipe.transform(parseInt(value, 10));
  }

  private unlocalize(value: any): number {
    if (typeof value === 'number') {
      value = value.toFixed(0);
    }
    return parseInt(value.toString().replace(',0', '').split(',').join('').split('.').join(''), 10);
  }

  private validate(sum: number, meters: EfficiencyAnalysisMeters[]): void {
    this.nextButtonStatus.emit(!this.isValid(sum, meters));
  }

  private isValid(sum: number, meters: EfficiencyAnalysisMeters[]): boolean {
    const isComplete = isNil(meters.find((eaMeters: EfficiencyAnalysisMeters) => eaMeters.isSelected && this.isPropNotValid(eaMeters.value)));
    const hasNoMeter = !isNil(
      meters.find(
        (eaMeters: EfficiencyAnalysisMeters) =>
          this.meterTemplateConfig === EfficiencyAnalysisElectricityMeterConfig.none && this.isPropNotValid(eaMeters.meterNumber) && !isNil(eaMeters.value)
      )
    );
    const isValid = sum > 0 && ((meters.length >= 1 && isComplete) || hasNoMeter);
    return isValid;
  }

  private loadMeterTemplateConfig(electrictyMeterConsumption: EfficiencyAnalysisMeters[]): void {
    if (electrictyMeterConsumption.length === 1) {
      const meter = electrictyMeterConsumption[0].meter;
      if (this.isPropNotValid(meter.measurementLocationKey) && this.isPropNotValid(meter.meterNumber)) {
        this.meterTemplateConfig = EfficiencyAnalysisElectricityMeterConfig.none;
      } else {
        this.meterTemplateConfig = EfficiencyAnalysisElectricityMeterConfig.single;
      }
    } else {
      this.meterTemplateConfig = EfficiencyAnalysisElectricityMeterConfig.multiple;
    }
  }

  private numberOnly(event: KeyboardEvent): boolean {
    const allowInput = /^\d$/.test(event.key);

    if (!allowInput) {
      return false;
    }

    const inputElement = event.target as HTMLInputElement;
    const valueLength = inputElement.value.length + 1;

    return valueLength <= 7;
  }

  private isPropNotValid(prop: any): boolean {
    return isNil(prop) || isEmpty(prop);
  }

  private areDatesSimilar(date1: string | Date, date2: string | Date): boolean {
    return DateUtilities.toExpectedFormat(date1, DotDayMonthYearFormat) === DateUtilities.toExpectedFormat(date2, DotDayMonthYearFormat);
  }

  private getDisplayDate(date: string | Date): string {
    return DateUtilities.toExpectedFormat(date, DotDayMonthYearFormat);
  }
}
